/**
 * What an enrichment run touched, grouped by entity type.
 *
 * Replaces the scrape page's "Created entities" panel, which reads
 * `created_entities` / `created_event_id` — both null on every job row, because
 * enrichment updates an entity that already exists rather than creating one. So
 * the panel said "No created entities recorded for this run" on every
 * enrichment run ever opened.
 *
 * The entity id is the link a curator wants (it goes to the entity's own IMS
 * page); the job row behind it stays reachable from the table above.
 *
 * The scroll area is capped rather than fixed at 360px: the p95 enrichment run
 * has two entities, and a fixed box reserved for them is mostly empty air.
 */
import { ArrowUpRight, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { makeTooltip } from "@/components/ui/tooltip";
import { FINAL_OUTCOME_LABEL } from "@/lib/tina/constants";
import type {
  EnrichmentRunEntity,
  EnrichmentRunFacts,
} from "@/pages/tina/runs/lib/enrichment-run-facts";

interface EnrichmentEntitiesPanelProps {
  facts: EnrichmentRunFacts | null;
  isLoading: boolean;
}

export function EnrichmentEntitiesPanel({
  facts,
  isLoading,
}: EnrichmentEntitiesPanelProps) {
  const groups = facts?.groups ?? [];
  const total = groups.reduce((sum, group) => sum + group.entities.length, 0);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Entities in this run</h2>
          <Badge variant="secondary">{total}</Badge>
        </div>
        <span className="text-[11px] text-muted-foreground">
          one row per entity, not per job
        </span>
      </div>
      <CardContent className="p-3">
        {isLoading ? (
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-28 rounded-md" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
            No enrichment jobs recorded for this run.
          </div>
        ) : (
          <ScrollArea className="max-h-[360px]">
            <div className="grid gap-3 pr-3 xl:grid-cols-2 2xl:grid-cols-3">
              {groups.map((group) => (
                <div
                  key={group.entityType}
                  className="rounded-md border bg-card"
                >
                  <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground">
                      {group.label}
                    </h3>
                    <Badge variant="secondary">{group.entities.length}</Badge>
                  </div>
                  <div className="divide-y">
                    {group.entities.map((entity) => (
                      <EntityRow
                        key={`${entity.entityType}:${entity.entityId}`}
                        entity={entity}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

function EntityRow({ entity }: { entity: EnrichmentRunEntity }) {
  const outcome = entity.outcome
    ? (FINAL_OUTCOME_LABEL[entity.outcome] ?? entity.outcome)
    : "In flight";
  const body = (
    <>
      <div className="min-w-0">
        <p className="truncate font-mono text-xs font-medium">
          {entity.entityId}
        </p>
        <p className="truncate text-[11px] text-muted-foreground">
          {entity.modeLabel} · {outcome}
        </p>
      </div>
      {entity.href && (
        <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
    </>
  );

  // A job that gave up is the one row a curator has to act on, and only
  // artist / venue / event have a review page to act on it in.
  const review =
    entity.outcome === "manual_review" && entity.reviewHref ? (
      <a
        href={entity.reviewHref}
        onClick={(event) => event.stopPropagation()}
        className="shrink-0 text-[11px] font-medium text-blue-500 hover:underline"
      >
        review
      </a>
    ) : null;

  const row = entity.href ? (
    <a
      href={entity.href}
      className="flex min-w-0 flex-1 items-center justify-between gap-2 hover:underline"
    >
      {body}
    </a>
  ) : (
    <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
      {body}
    </div>
  );

  return makeTooltip(
    <div className="flex items-center gap-2 px-3 py-2 hover:bg-accent">
      {row}
      {review}
    </div>,
    `item ${entity.itemId}`,
  );
}
